// components/reports/PgxReport.js
// CRA/MUI port (inline styles) of the marketing site's PGX/Drug Metabolism
// report. "PGX" and "Drug Metabolism" are the same report_type/product —
// this one component renders BOTH shapes that can land under it:
//   - `genes` (Gene/Genotype/Phenotype) — the generic schema used by
//     AIden/Claire chat analysis of a CURRENT-format report (modeled on the
//     real "Proove sample report.pdf") and by the $99 other-lab upload.
//     Dosage guidance for this shape lives entirely inside the AI-generated
//     clinical summary prose, since that pass reads the report's page
//     images directly.
//   - `findings` (Enzyme/Grade/Medications/Drug-Drug Interactions/Genotype)
//     — 2026-09-17: the free old->new conversion of Proove's OLD "Proof of
//     Drug Metabolism" report layout, which prints these as real structured
//     columns rather than folding them into prose. Whichever the backend
//     extracted is what renders here — a record never has both populated.
import React from "react";
import ReportShell from "./ReportShell";
import { PGX_COPY, AI_ANALYSIS_DISCLAIMER_HTML } from "./reportCopy";
import { colorizeRiskTerms } from "./reportHelpers";
import { sectionBar, innerCard, table, th, td } from "./reportStyles";

export default function PgxReport({ extracted, clinicalSummaryHtml, disclaimer, mismatchNotice, reportDate, variant, analyzedBy }) {
  const genes = extracted?.genes || [];
  const findings = extracted?.findings || [];
  const hasFindings = findings.length > 0;
  // 2026-09-21/22 — new-format-report shape only (never populated alongside
  // `findings`, the old-report shape): Current Patient Medications / Risk
  // Management, structurally extracted and shown here as their own
  // sections. Dosing Guidance is deliberately NOT a separate section here —
  // a real report can have ~20 individual dosing-guidance cards, so it's
  // extracted as one condensed prose summary (dosing_guidance_summary)
  // instead, which feeds straight into the AI summary's own Dosing Guidance
  // section rather than being shown twice.
  const riskManagement = extracted?.risk_management || [];
  const currentMeds = extracted?.current_patient_medications;

  return (
    <ReportShell
      title="Proove Drug Metabolism"
      subtitle="Drug Metabolism Profile"
      variant={variant}
      analyzedBy={analyzedBy}
      patient={extracted?.demographics}
      reportDate={reportDate}
      methodology={PGX_COPY.methodology}
      disclaimer={PGX_COPY.disclaimer}
      interpretationNode={
        <div style={{ whiteSpace: "pre-line", padding: "12px 16px", fontSize: "0.82rem", lineHeight: 1.6, color: "#575555" }}>
          {PGX_COPY.interpretation}
        </div>
      }
    >
      {mismatchNotice && (
        <div style={{ marginBottom: 12, borderRadius: 8, border: "1px solid #fcd34d", background: "#fffbeb", padding: "8px 12px", fontSize: "0.82rem", color: "#92400e" }}>
          {mismatchNotice}
        </div>
      )}

      <div style={innerCard}>
        <div style={sectionBar}>Proove AI Powered-Clinical Summary</div>
        <div style={{ padding: "12px 16px", fontSize: "0.85rem", lineHeight: 1.65, color: "#333" }}>
          {clinicalSummaryHtml ? (
            <div className="ai-summary-html" dangerouslySetInnerHTML={{ __html: colorizeRiskTerms(clinicalSummaryHtml) }} />
          ) : (
            <p style={{ color: "#9a9a9a" }}>AI summary temporarily unavailable — showing extracted results only.</p>
          )}
        </div>
        <div style={{ borderTop: "1px solid #eee", padding: "12px 16px", fontSize: "0.78rem", lineHeight: 1.6, color: "#6b7280" }}
          dangerouslySetInnerHTML={{ __html: disclaimer || AI_ANALYSIS_DISCLAIMER_HTML }} />
      </div>

      {hasFindings && (
        <div style={{ ...innerCard, marginTop: 16 }}>
          <div style={sectionBar}>Test Details</div>
          <table style={table}>
            <thead>
              <tr>
                <th style={{ ...th, background: "#ececec", color: "#575555" }}>Enzyme</th>
                <th style={{ ...th, background: "#ececec", color: "#575555" }}>Grade</th>
                <th style={{ ...th, background: "#ececec", color: "#575555" }}>Medications</th>
                <th style={{ ...th, background: "#ececec", color: "#575555" }}>Drug-Drug Interactions</th>
                <th style={{ ...th, background: "#ececec", color: "#575555" }}>Genotype</th>
              </tr>
            </thead>
            <tbody>
              {findings.map((f, i) => (
                <tr key={i}>
                  <td style={{ ...td, fontWeight: 600 }}>{f.enzyme}</td>
                  <td style={td}>{f.grade || "—"}</td>
                  <td style={td}>{f.medications || "—"}</td>
                  <td style={td}>{f.drug_drug_interactions || "—"}</td>
                  <td style={td}>{f.genotype || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!hasFindings && currentMeds && (
        <div style={{ ...innerCard, marginTop: 16 }}>
          <div style={sectionBar}>Current Patient Medications</div>
          <div style={{ padding: "12px 16px", fontSize: "0.82rem", lineHeight: 1.6, color: "#575555" }}>
            {currentMeds}
          </div>
        </div>
      )}

      {!hasFindings && riskManagement.length > 0 && (
        <div style={{ ...innerCard, marginTop: 16 }}>
          <div style={sectionBar}>Risk Management</div>
          <table style={table}>
            <thead>
              <tr>
                <th style={{ ...th, background: "#ececec", color: "#575555" }}>Condition</th>
                <th style={{ ...th, background: "#ececec", color: "#575555" }}>Finding</th>
              </tr>
            </thead>
            <tbody>
              {riskManagement.map((r, i) => (
                <tr key={i}>
                  <td style={{ ...td, fontWeight: 600 }}>{r.condition}</td>
                  <td style={td}>{r.finding || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!hasFindings && genes.length > 0 && (
        <div style={{ ...innerCard, marginTop: 16 }}>
          <div style={sectionBar}>Test Details</div>
          <table style={table}>
            <thead>
              <tr><th style={{ ...th, background: "#ececec", color: "#575555" }}>Gene</th><th style={{ ...th, background: "#ececec", color: "#575555" }}>Genotype</th><th style={{ ...th, background: "#ececec", color: "#575555" }}>Phenotype</th></tr>
            </thead>
            <tbody>
              {genes.map((g, i) => (
                <tr key={i}>
                  <td style={{ ...td, fontWeight: 600 }}>{g.gene}</td>
                  <td style={td}>{g.genotype || "—"}</td>
                  <td style={td}>{g.phenotype || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!hasFindings && genes.length === 0 && riskManagement.length === 0 && !currentMeds && !extracted?.dosing_guidance_summary && (
        <div style={{ marginTop: 16, borderRadius: 12, border: "1px dashed #e2e2e2", padding: 16, textAlign: "center", fontSize: "0.82rem", color: "#9a9a9a" }}>
          No gene-specific findings were found in this report.
        </div>
      )}
    </ReportShell>
  );
}
